import React from 'react'
import { createPortal } from 'react-dom'
import styled from '@emotion/styled'
import { font, colors, spacing } from '@mobi/component-library/Theme/Common'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { EditMyProfile } from '@core/Areas/FollowMe/Components/EditMyProfile'
import {
  trackFollowMeEditAvatar,
  trackFollowMeEditMyProfile,
  trackFollowMeProfileTabAndPillChange,
} from '@core/Areas/FollowMe/helpers/analytics'
import { FollowButton } from '../Common/FollowButton/FollowButton'
import { FollowMeProfile } from '../../types'
import { ProfileAvatar } from '../Common/ProfileAvatar'
import { ProfileTips } from './Components/Tips/ProfileTips'
import { EmptyProfile } from './Components/EmptyProfile/EmptyProfile'

const enum LocalConstants {
  TruncatedTextLength = 160,
}

export const ProfileDisplay: React.FC<{
  isMyProfile: boolean
  profile: FollowMeProfile
}> = ({ isMyProfile, profile }) => {
  const [isTrayOpen, setIsTrayOpen] = React.useState(false)
  const [isFullBioShown, setIsFullBioShown] = React.useState(false)
  const [isEditingProfile, setIsEditingProfile] = React.useState(true)
  const [selectedTab, setSelectedTab] = React.useState<'Tips' | 'Stats'>('Tips')
  const [hasProfileCopiedBets, setHasProfileCopiedBets] = React.useState(false)

  const onEditProfileClick = () => {
    setIsEditingProfile(true)
    setIsTrayOpen(true)
    trackFollowMeEditMyProfile()
  }

  const onEditAvatarClick = () => {
    setIsEditingProfile(!isEditingProfile)
    isEditingProfile && trackFollowMeEditAvatar()
  }

  const onDismissEditProfile = () => {
    setIsTrayOpen(false)
  }

  const profileBio = profile?.description ?? ''
  const shouldEnableBioTruncateToggle = profileBio.length > LocalConstants.TruncatedTextLength
  const profileBioDisplayText =
    !isFullBioShown && shouldEnableBioTruncateToggle
      ? `${profileBio.slice(0, LocalConstants.TruncatedTextLength).trim()}…`
      : profileBio

  const title = isEditingProfile ? 'Edit your profile' : 'Edit Avatar'

  const setHasCopiedBets = (bool: boolean) => {
    setHasProfileCopiedBets(bool)
  }

  const profileEmptyState = isMyProfile
    ? profile.shareCount === 0 && !hasProfileCopiedBets
    : profile.shareCount === 0

  return (
    <>
      <WrapperStyled>
        <ProfileAvatar size='lg' color={profile.color} avatar={profile.avatar} />
        <h2>{profile.displayName}</h2>

        <div className='follow-me__profile-stats'>
          <div>
            <span>{profile?.followerCount || 0}</span>
            <span>Followers</span>
          </div>

          <div>
            <span>{profile.followingCount}</span>
            <span>Following</span>
          </div>

          <div>
            <span>{profile.shareCount}</span>
            <span>Shared Bets</span>
          </div>
        </div>

        {profileBio ? (
          <p>
            {profileBioDisplayText}{' '}
            {shouldEnableBioTruncateToggle ? (
              <button type='button' onClick={() => setIsFullBioShown(prev => !prev)}>
                {isFullBioShown ? 'Show Less' : 'Read More'}
              </button>
            ) : null}
          </p>
        ) : null}

        <div className='follow-me__profile-actions'>
          {isMyProfile ? (
            <Button color='secondary_outline' size='sm' isFullWidth onClick={onEditProfileClick}>
              Edit Profile
            </Button>
          ) : (
            <FollowButton
              shouldShowUnfollow
              accountId={profile.id}
              isFollowing={profile.isFollowing}
              isNotificationsEnabled={profile.isNotificationsEnabled}
            />
          )}
        </div>
      </WrapperStyled>

      {createPortal(
        <Tray
          isOpen={isTrayOpen}
          headerTitle={title}
          onDismiss={onDismissEditProfile}
          onBackNavigate={
            !isEditingProfile ? () => setIsEditingProfile(!isEditingProfile) : undefined
          }
        >
          <div style={{ marginBottom: '2rem' }}>
            <EditMyProfile
              onEditAvatarClick={onEditAvatarClick}
              isEditingProfile={isEditingProfile}
              onSuccessCallback={() => setIsTrayOpen(false)}
            />
          </div>
        </Tray>,
        document.body
      )}

      {profileEmptyState ? (
        <EmptyProfile isMyProfile={isMyProfile} />
      ) : (
        <>
          <Tabs>
            <TabButton
              content='Tips'
              isSelected={selectedTab === 'Tips'}
              onClick={() => {
                setSelectedTab('Tips')
                trackFollowMeProfileTabAndPillChange('Tab Change', 'Tips', 'Pending')
              }}
            />

            <TabButton
              content='Stats'
              isSelected={selectedTab === 'Stats'}
              onClick={() => {
                setSelectedTab('Stats')
                trackFollowMeProfileTabAndPillChange('Tab Change', 'Stats')
              }}
            />
          </Tabs>

          {selectedTab === 'Tips' && (
            <ProfileTips
              isMyProfile={isMyProfile}
              profile={profile}
              setHasCopiedBets={setHasCopiedBets}
            />
          )}
        </>
      )}
    </>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: spacing.md,
  margin: '0 auto',
  padding: spacing.md,
  fontFamily: font.family.primary,
  backgroundColor: colors.white,

  '> h2': {
    fontSize: font.size.xl2.fontSize,
    lineHeight: font.size.xl2.lineHeight,
    letterSpacing: font.size.xl2.letterSpacing,
    fontWeight: font.weight.semibold,
    color: colors.black,
    textShadow: 'none',
    textTransform: 'unset',
    width: '100%',
    textAlign: 'center',
  },

  '> div.follow-me__profile-stats': {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    margin: '0 auto',
    textAlign: 'center',

    '> div': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: `0 ${spacing.sm}`,

      '&:nth-of-type(2)': {
        borderRight: `1px solid ${colors.neutral[200]}`,
        borderLeft: `1px solid ${colors.neutral[200]}`,
      },

      '> span:first-of-type': {
        fontSize: font.size.xl2.fontSize,
        letterSpacing: font.size.xl2.letterSpacing,
        lineHeight: font.size.xl2.lineHeight,
        fontWeight: font.weight.semibold,
        color: colors.black,
      },

      '> span:last-of-type': {
        fontSize: font.size.md.fontSize,
        letterSpacing: font.size.md.letterSpacing,
        lineHeight: font.size.md.lineHeight,
        fontWeight: font.weight.regular,
        color: colors.neutral[700],
      },
    },
  },

  // Bio
  '> p': {
    fontSize: font.size.md.fontSize,
    letterSpacing: font.size.md.letterSpacing,
    lineHeight: font.size.md.lineHeight,
    fontWeight: font.weight.regular,
    color: colors.neutral[700],
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
    textAlign: 'start',

    '> button': {
      fontSize: 'inherit',
      fontWeight: font.weight.semibold,
      color: colors.neutral[800],
      cursor: 'pointer',
      textDecoration: 'underline',
      whiteSpace: 'nowrap',
      padding: 0,
      background: 0,
      border: 0,
    },
  },

  '.follow-me__profile-actions': {
    alignSelf: 'stretch',
    display: 'flex',
    gap: spacing.smx1,

    '> button:first-of-type': { flex: 1 },
  },
})
